import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, set, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
// import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroupAccount } from 'src/shared/models/invoiceGroup/invoiceGroupAccount';
import { createInvoiceGroupAccounts, getInvoiceGroupAccountsById, updateInvoiceGroupAccounts } from 'src/shared/services/invoiceGroupService';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { toast } from 'react-toastify';
import { useSchemaInvoiceGroupAccount } from '../schemas/useSchemaInvoiceGroupAccount';

export const AdminInvoiceGroupAccountingTab = () => {
  const params = useParams();
  const { t } = useTranslation(['invoice']);
  const [loading, setLoading] = useState(true);
  const isMounted = useIsMounted();
  const [invoiceGroupAccounts, setInvoiceGroupAccounts] = useState<InvoiceGroupAccount>();
  const [hasExistingAccount, setHasExistingAccount] = useState(false);

  const invoiceGroupAccountSchema = useSchemaInvoiceGroupAccount();
  type InvoiceGroupAccountInterface = InferType<typeof invoiceGroupAccountSchema>;

  const formMethods = useForm<InvoiceGroupAccountInterface>({
    resolver: yupResolver(invoiceGroupAccountSchema),
    mode: 'all',
    defaultValues: {
      bankAccount: '',
      memberAccount: '',
      depositAccount: '',
      fsPayServiceAccount: '',
      voucherSerie: '',
      eventAccount: '',
      buyAccount: '',
      clearingSwishAccount: '',
      clearingStripeAccount: '',
    },
  });

  const { handleSubmit, formState, reset, trigger } = formMethods;
  const { isDirty, isValid } = formState;
  const fetchInvoiceGroupAccounting = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getInvoiceGroupAccountsById(id);
        if (!isMounted()) return;
        // console.log('fetchInvoiceGroupAccounting result', result);
        const account = result.data[0];
        setInvoiceGroupAccounts(account);
        setHasExistingAccount(!!account);
        reset({
          bankAccount: account?.bankAccount ?? '',
          memberAccount: account?.memberAccount ?? '',
          depositAccount: account?.depositAccount ?? '',
          fsPayServiceAccount: account?.fsPayServiceAccount ?? '',
          voucherSerie: account?.voucherSerie ?? '',
          eventAccount: account?.eventAccount ?? '',
          buyAccount: account?.buyAccount ?? '',
          clearingSwishAccount: account?.clearingSwishAccount ?? '',
          clearingStripeAccount: account?.clearingStripeAccount ?? '',
        });
        await trigger();
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, reset, trigger],
  );

  useEffect(() => {
    if (!params.id) return;
    fetchInvoiceGroupAccounting(+params.id);
  }, [params.id, fetchInvoiceGroupAccounting]);

  const onSubmit = async (data: InvoiceGroupAccountInterface) => {
    setLoading(true);
    const dataToSubmit: InvoiceGroupAccount = {
      ...data,
      ownerConfigAccount_id: invoiceGroupAccounts?.ownerConfigAccount_id ?? 0,
      owner_id: invoiceGroupAccounts?.owner_id ?? params.id ? Number(params.id) : 0,
    };
    try {
      if (hasExistingAccount) {
        await updateInvoiceGroupAccounts(dataToSubmit);
      } else {
        // ConsoleHelper.log('Creating new invoice group account with data:', dataToSubmit);
        await createInvoiceGroupAccounts(dataToSubmit);
      }
      if (isMounted()) {
        toast.success(<>{t('invoice:adminInvoiceGroupAccountingTab.saveSuccess')}</>);
        fetchInvoiceGroupAccounting(Number(params.id));
      }
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(<>{t('invoice:adminInvoiceGroupAccountingTab.saveError')}</>);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {/* <LoadingOverlay loading={loading} /> */}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {!loading && (
              <>
                <Grid container spacing={5}>
                  <Grid item xs={12} md={12} lg={7}>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.bankAccountTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.bankAccountDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField label={t('invoice:adminInvoiceGroupAccountingTab.bankAccountLabel')} name='bankAccount' />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.revenueAccountTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.revenueAccountDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField label={t('invoice:adminInvoiceGroupAccountingTab.memberAccountLabel')} name='memberAccount' />
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        <ControlledTextField label={t('invoice:adminInvoiceGroupAccountingTab.despositLabel')} name='depositAccount' />
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        <ControlledTextField label={t('invoice:adminInvoiceGroupAccountingTab.eventAccountLabel')} name='eventAccount' />
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        <ControlledTextField label={t('invoice:adminInvoiceGroupAccountingTab.buyAccountLabel')} name='buyAccount' />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.receivableTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.receivableDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField
                          label={t('invoice:adminInvoiceGroupAccountingTab.clearingSwishAccountLabel')}
                          name='clearingSwishAccount'
                        />
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        <ControlledTextField
                          label={t('invoice:adminInvoiceGroupAccountingTab.clearingStripeAccountLabel')}
                          name='clearingStripeAccount'
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider />
                <Grid display='flex' justifyContent='flex-end' mt={2}>
                  <Button type='submit' variant='contained' disableElevation disabled={!isDirty || !isValid || loading}>
                    {t('common:button.save')}
                  </Button>
                </Grid>
              </>
            )}
          </form>
        </FormProvider>
      </Box>
    </>
  );
};
